import React, { useEffect, useState, useRef, useReducer } from "react";
import { useParams, useNavigate } from "react-router";
import SearchForm from "../../stories/Forms/SearchForm";
import {
  searchFormFields,
  ConfirmationOneTubeFields,
  ConfirmationTwoTubeFields,
  clinicalPatientConfirmationFields,
  links,
} from "../../stories/Forms/fields";
import { formatDtAge, formatStudyName } from "../../utils/utils";
import useApis from "../../services/useAPI";
import { useAuth } from "../../components/AuthContext/AuthContext";
import logo from "../../assets/images/boots-myhealthchecked-logo.svg";
import img from "../../assets/images/notification.svg";
import LoggedInLayout from "../../stories/LoggedInScreens/LoggedInLayout";
import { ArrowRightCircleFill, XCircleFill } from "react-bootstrap-icons";

/**
 * ConfirmDetails component fetches and displays test confirmation details and allows
 * submission of test confirmation data.
 * @returns {JSX.Element} - Rendered ConfirmDetails component.
 */
function ConfirmDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const formikRef = useRef(null);

  // Hook for API calls and managing state
  const {
    callApi,
    resultGet,
    message,
    isLoading,
    serverError,
    showError,
    tokenError,
    toggleShowError,
  } = useApis();

  // State management for various data
  const [userData, setUserData] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [storeOptions, setStoreOptions] = useState([]);
  const getUrl = `/api/phlebotomists-portal/test-confirmation/${id}/`;
  const postUrl = `/api/phlebotomists-portal/test-confirmation/${id}/`;
  const clinicalurl = `/api/phlebotomists-portal/clinical-test-confirmation/${id}/`;
  const [selectedStore, setSelectedStore] = useState(null);
  const [, setSelectedOption] = useReducer((_, value) => value, null);
  const [fields, setFields] = useState(clinicalPatientConfirmationFields);
  const isPatientCard = localStorage.getItem("patientCard") === "true";
  const vhSelectedStore = JSON.parse(sessionStorage.getItem("selectedStore") || "null");

  // Get the authentication context
  const auth = useAuth();
  let selectedService = localStorage.getItem("selectedService");
  const stages = ["patient-details","confirm-details","enter-barcode","appointment-confirmation"];
  
  useEffect(() => {
    if (!stages.includes(sessionStorage.getItem("allowedStage")) && auth?.featureFlag) {
      navigate("/patient-record");
    }
  },[sessionStorage.getItem("allowedStage"), auth?.featureFlag, navigate]);

  useEffect(() => {
    if (serverError || tokenError) {
      auth?.showToast(
        "Error",
        tokenError || serverError,
        "../assets/mhc.png",
        {icon: <XCircleFill />},
      );
    }
  }, [serverError, tokenError]);

  useEffect(() => {
    if (selectedService === "healthCare") {
      setFields(searchFormFields);
    } else {
      setFields(clinicalPatientConfirmationFields);
    }
  }, [selectedService, searchFormFields, clinicalPatientConfirmationFields]);

  // Handle store selection change
  const handleSelectChange = (selectedOption) => {
    setSelectedOption(selectedOption);
    setSelectedStore(selectedOption);
  };

  // Handle form submission
  const handleSubmit = async (data) => {
    let url = postUrl;
    if (selectedService === "clinical") {
      url = clinicalurl;
    }

    const storeNumber = auth?.featureFlag ? vhSelectedStore.store_number : data?.location_store.split(" ")[0];
    const storeName = auth?.featureFlag ? vhSelectedStore.store_name : data?.location_store.split(" ").slice(2).join(" ");
    const study = formatStudyName(data?.study);

    const apiData = {
    store_number: storeNumber,
    store_name: storeName,
      ...(study && { study }),
    };
    await callApi(apiData, url, "POST", {
      Authorization: `Bearer ${
        localStorage.getItem("access_token") || auth.accessToken
      }`,
      "Content-Type": "application/json",
    });
    auth?.allowStage("confirm-details");
  };

  useEffect(() => {
    /**
     * Fetches test confirmation data based on the id parameter.
     */
    let url = getUrl;
    if (selectedService === "clinical") {
      auth.setTitle("Clinical studies");
      url = clinicalurl;
    }
    const fetchData = async () => {
      await callApi("", url, "GET", {
        Authorization: `Bearer ${
          localStorage.getItem("access_token") || auth.accessToken
        }`,
        "Content-Type": "application/json",
      });
    };

    fetchData();
  }, [id]);

  // Populate the form fields when resultGet is available
  useEffect(() => {
    if (resultGet) {
      if (selectedService === "healthCare") {
        auth.setTitle(
          `In-store Wellness Testing Service - ${resultGet?.booking_info?.test_type}`,
        );
      }
      const { user_info, booking_info, store_locations } = resultGet;
      const vesselTypes = [];

      if (booking_info.vessel_type_1 && booking_info.vessel_amount_1) {
        vesselTypes.push(
          `${booking_info.vessel_type_1}, ${booking_info.vessel_amount_1}`,
        );
      }

      if (booking_info.vessel_type_2 && booking_info.vessel_amount_2) {
        vesselTypes.push(
          `${booking_info.vessel_type_2}, ${booking_info.vessel_amount_2}`,
        );
      }

      setBookingData(booking_info);
      const formattedUserData = {
        ...user_info,
        dob: formatDtAge(user_info.dob),
        repeated_test: booking_info.repeated_test,
        vessel_type: vesselTypes.join("\n"),
        vessel_info_1: booking_info?.vessel_info_1,
        vessel_info_2: booking_info?.vessel_info_2,
        test_type: booking_info?.test_type,
      };
      setUserData(formattedUserData);
      const storeOpts = auth?.featureFlag
        ? [
            {
              value: `${vhSelectedStore?.store_number} ${vhSelectedStore?.store_name}`,
              label: `${vhSelectedStore?.store_number} ${vhSelectedStore?.store_name}`,
            },
          ]
        : store_locations.map((location) => ({
            value: location.store_info,
            label: location.store_info,
          }));
      setStoreOptions(storeOpts);

      const selected =
        storeOpts.find((opt) => opt.value === resultGet.store_location_value) ||
        storeOpts[0];

      setSelectedStore(selected || null);
    }

    // Handle post submission response
    if (message?.postMessage) {
      auth?.showToast(
        "Confirm user details",
        "User details confirmed!",
        "../assets/mhc.png",
        {icon: <ArrowRightCircleFill />},
      );
      navigate(`/enter-barcode/${id}`);
    } else {
      toggleShowError();
    }
  }, [resultGet, message]);

  const description = "Please confirm details to continue registration";

  // Navigates back to the previous page
  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <div className="test-confirmation-form">
      <LoggedInLayout
        buttonLabel={"Logout"}
        img={img}
        isActivation={false}
        links={links}
        logo={logo}
        pageTitle={auth.title}
        userInitials={auth.userInitials}
      >
        {isLoading && <p>Loading...</p>}
        {/* <pre>{JSON.stringify(resultGet, null, 2)}</pre> */}
        <SearchForm
          formikRef={formikRef}
          title={
            selectedService === "clinical"
              ? "Participant record"
              : "Store location"
          }
          data={userData}
          colStructure={3}
          subtitle={
            selectedService === "healthCare"
              ? "Select a store location from the dropdown"
              : "Please confirm details to continue registration"
          }
          formClass={`details-form ${
            isPatientCard && "thrive-clinical-content"
          } `}
          description={selectedService === "clinical" && description}
          fields={
            selectedService === "clinical"
              ? fields.map((field) =>
                  field.name === "location_store"
                    ? { ...field, options: storeOptions }
                    : field,
                )
              : [
                  ...(resultGet?.booking_info?.vessel_info_2
                    ? ConfirmationTwoTubeFields
                    : ConfirmationOneTubeFields
                  ).map((field) =>
                    field.name === "location_store"
                      ? { ...field, options: storeOptions }
                      : field,
                  ),
                ]
          }
          onBackClick={handleBackClick}
          onSelectChange={handleSelectChange}
          onSubmit={handleSubmit}
          storeName={selectedStore}
        />
        <div className="outside-actions mx-2">
          <button
            className="btn btn-outline btn-back"
            onClick={handleBackClick}
          >
            Back
          </button>

          <button
            className="btn btn-primary btn-rounded"
            onClick={() => formikRef.current?.submitForm()}
          >
            Save & Continue
          </button>
        </div>
        {/* <SearchForm
              buttonGroup={true}
              className={"custom-test-confirmation-grid"}
              colStructure={3}
              data={bookingData}
              fields={(resultGet?.booking_info?.vessel_info_2
                ? ConfirmationTwoTubeFields
                : ConfirmationOneTubeFields
              ).map((field) =>
                field.name === "location_store"
                  ? { ...field, options: storeOptions }
                  : field
              )}
              onBackClick={handleBackClick}
              onSelectChange={handleSelectChange}
              onSubmit={handleSubmit}
              storeName={selectedStore}
            /> */}
      </LoggedInLayout>
    </div>
  );
}

export default ConfirmDetails;
